import { useEffect, useRef } from "react";
import { OutlineActionsBlock, OutlineLabelBlock, OutlinePlusBlock } from "./OutlinePartial";

// --- HELPERS ---
const isFixedElement = (el) => {
    for (let node = el; node && node !== document.documentElement; node = node.parentNode) {
        const pos = window.getComputedStyle(node).position;
        if (pos === 'fixed' || pos === 'sticky') return true;
    }
    return false;
};

const capitalize = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : '';

const applyOutlineStyle = (outlineEl, targetEl, isFixed) => {
    if (!outlineEl) return;
    if (!targetEl) {
        outlineEl.style.display = 'none';
        return;
    }

    const rect = targetEl.getBoundingClientRect();
    let top = rect.top, left = rect.left;

    if (!isFixed) {
        for (let p = targetEl.parentNode; p && p !== document.documentElement; p = p.parentNode) {
            top += p.scrollTop || 0;
            left += p.scrollLeft || 0;
        }
        top += window.scrollY;
        left += window.scrollX;
    }

    Object.assign(outlineEl.style, {
        position: isFixed ? 'fixed' : 'absolute',
        top: `${top}px`, left: `${left}px`,
        width: `${rect.width}px`, height: `${rect.height}px`,
        display: 'block'
    });

    outlineEl.classList.toggle('__top', rect.top <= 0);
    outlineEl.classList.toggle('__bottom-inside', (rect.bottom + 50) > window.innerHeight);
};

// Tìm tất cả block "anh em sinh đôi" cùng smind-block-id trong cùng section-wrapper
// (ví dụ slider clone). Trả về [{ el, isFixed }] KHÔNG bao gồm primary.
const buildMirrors = (primary) => {
    if (!primary) return [];
    const blockId = primary.getAttribute('smind-block-id');
    const sectionWrapper = primary.closest('section-wrapper');
    if (!blockId || !sectionWrapper) return [];

    const all = sectionWrapper.querySelectorAll(`[smind-block-id="${blockId}"]:not([data-smind-dead="true"])`);
    const mirrors = [];
    all.forEach((el) => {
        if (el !== primary) mirrors.push({ el, isFixed: isFixedElement(el) });
    });
    return mirrors;
};

// Trả về vị trí của block trong list cùng smind-block-id bên trong section-wrapper.
// Dùng để sau này re-render xong vẫn pick đúng "đứa" đã được active/hover (primary vs clones).
const getBlockIndex = (block) => {
    if (!block) return -1;
    const blockId = block.getAttribute('smind-block-id');
    const wrapper = block.closest('section-wrapper');
    if (!blockId || !wrapper) return -1;
    const all = Array.from(wrapper.querySelectorAll(`[smind-block-id="${blockId}"]:not([data-smind-dead="true"])`));
    return all.indexOf(block);
};

// Ghi lại "đường đi" (chuỗi chỉ số con) từ section-wrapper xuống node. Path ổn định giữa
// các lần re-render cùng schema — dù Swiper clones, tab visibility đổi... path vẫn trỏ đúng.
const getDOMPath = (el, root) => {
    const path = [];
    let cur = el;
    while (cur && cur !== root && cur.parentNode) {
        const parent = cur.parentNode;
        const idx = Array.prototype.indexOf.call(parent.children, cur);
        if (idx < 0) return null;
        path.unshift(idx);
        cur = parent;
        if (!cur) return null;
    }
    return cur === root ? path : null;
};

const findByDOMPath = (root, path) => {
    if (!root || !path || !path.length) return null;
    let cur = root;
    for (const idx of path) {
        if (!cur || !cur.children || !cur.children[idx]) return null;
        cur = cur.children[idx];
    }
    return cur;
};

// Self-heal ưu tiên: (1) DOM path — chính xác nhất. (2) index trong list cùng block-id.
// (3) fallback node đầu tiên. Verify bằng blockId để đảm bảo đúng block.
const findBlockSmart = (sectionId, blockId, domPath, index) => {
    if (!sectionId || !blockId) return null;
    const wrapper = document.querySelector(`section-wrapper[section-id="${sectionId}"]`);
    if (!wrapper) return null;

    // 1. Theo DOM path
    const byPath = findByDOMPath(wrapper, domPath);
    if (byPath && byPath.getAttribute?.('smind-block-id') === blockId && !byPath.hasAttribute?.('data-smind-dead')) {
        return byPath;
    }

    // 2. Theo index
    const all = wrapper.querySelectorAll(`[smind-block-id="${blockId}"]:not([data-smind-dead="true"])`);
    if (!all.length) return null;
    if (index >= 0 && all[index]) return all[index];
    return all[0] || null;
};

// --- MAIN COMPONENT ---
// 💡 Thêm prop onSectionClick
function OutlineBlock({ activeId, onClick, onSectionClick, onHide, onRemove, onAdd, setSelectedElementId }) {
    const state = useRef({
        activeEl: null, activeIsFixed: false,
        activeSectionId: null, activeBlockId: null, activePrimaryIndex: -1, activeDOMPath: null,
        hoverEl: null, hoverIsFixed: false,
        hoverSectionId: null, hoverBlockId: null, hoverPrimaryIndex: -1, hoverDOMPath: null,
        hoverTimer: null, pendingHoverEl: null,
        activeMirrors: [], hoverMirrors: []
    }).current;

    // Remembers the exact DOM node the user clicked. Prevents the duplicate-block-id bug
    // where querySelector by smind-block-id returns the first match (e.g. slider clones).
    const lastClickedBlockRef = useRef(null);

    const refs = {
        hoverOut: useRef(null), activeOut: useRef(null),
        hoverTxt: useRef(null), activeTxt: useRef(null),
        hoverSecTxt: useRef(null), activeSecTxt: useRef(null),
        mirrorHost: useRef(null)
    };

    // Reconcile mirror divs: đảm bảo số div trong mirrorHost = hoverMirrors.length + activeMirrors.length
    // Div 0..n-1 cho active, n..n+m-1 cho hover. Tái sử dụng DOM nodes để tránh thrash.
    const reconcileMirrorDivs = () => {
        const host = refs.mirrorHost.current;
        if (!host) return;
        const total = state.activeMirrors.length + state.hoverMirrors.length;
        while (host.children.length < total) {
            const div = document.createElement('div');
            div.className = 'smind-outline-block __mirror';
            host.appendChild(div);
        }
        while (host.children.length > total) {
            host.removeChild(host.lastChild);
        }
        for (let i = 0; i < state.activeMirrors.length; i++) {
            const div = host.children[i];
            div.classList.add('--active');
            div.classList.remove('--hover');
        }
        for (let i = 0; i < state.hoverMirrors.length; i++) {
            const div = host.children[state.activeMirrors.length + i];
            div.classList.add('--hover');
            div.classList.remove('--active');
        }
    };

    useEffect(() => {
        let frameId;
        const update = () => {
            // Self-healing sau section re-render: nếu activeEl bị detach, tìm lại theo
            // (sectionId, blockId, DOMPath, primaryIndex) — ưu tiên DOM path để phân biệt
            // chính xác giữa các block cùng id (ví dụ tab-btn vs tab-content trong featured-collection).
            if (state.activeEl && !document.contains(state.activeEl)) {
                const healed = findBlockSmart(state.activeSectionId, state.activeBlockId, state.activeDOMPath, state.activePrimaryIndex);
                if (healed) {
                    state.activeEl = healed;
                    state.activeIsFixed = isFixedElement(healed);
                    const newWrapper = healed.closest('section-wrapper');
                    state.activeDOMPath = getDOMPath(healed, newWrapper);
                    state.activePrimaryIndex = getBlockIndex(healed);
                    state.activeMirrors = buildMirrors(healed);
                    updateLabels(healed, false);
                    if (refs.activeOut.current) refs.activeOut.current.classList.add('__active', '__show-actions');
                } else {
                    state.activeEl = null;
                    state.activeSectionId = null;
                    state.activeBlockId = null;
                    state.activePrimaryIndex = -1;
                    state.activeDOMPath = null;
                    state.activeMirrors = [];
                    if (refs.activeOut.current) {
                        refs.activeOut.current.style.display = 'none';
                        refs.activeOut.current.classList.remove('__active', '__show-actions');
                    }
                }
            }
            if (state.hoverEl && !document.contains(state.hoverEl)) {
                const healed = findBlockSmart(state.hoverSectionId, state.hoverBlockId, state.hoverDOMPath, state.hoverPrimaryIndex);
                if (healed) {
                    state.hoverEl = healed;
                    state.hoverIsFixed = isFixedElement(healed);
                    const newWrapper = healed.closest('section-wrapper');
                    state.hoverDOMPath = getDOMPath(healed, newWrapper);
                    state.hoverPrimaryIndex = getBlockIndex(healed);
                    state.hoverMirrors = buildMirrors(healed);
                } else {
                    state.hoverEl = null;
                    state.hoverSectionId = null;
                    state.hoverBlockId = null;
                    state.hoverPrimaryIndex = -1;
                    state.hoverDOMPath = null;
                    state.hoverMirrors = [];
                    if (refs.hoverOut.current) refs.hoverOut.current.style.display = 'none';
                }
            }

            // Dọn mirror chết trước khi vẽ
            state.activeMirrors = state.activeMirrors.filter(m => document.contains(m.el));
            state.hoverMirrors = state.hoverMirrors.filter(m => document.contains(m.el));

            applyOutlineStyle(refs.activeOut.current, state.activeEl, state.activeIsFixed);
            applyOutlineStyle(refs.hoverOut.current, state.hoverEl, state.hoverIsFixed);

            reconcileMirrorDivs();
            const host = refs.mirrorHost.current;
            if (host) {
                for (let i = 0; i < state.activeMirrors.length; i++) {
                    const m = state.activeMirrors[i];
                    applyOutlineStyle(host.children[i], m.el, m.isFixed);
                }
                for (let i = 0; i < state.hoverMirrors.length; i++) {
                    const m = state.hoverMirrors[i];
                    applyOutlineStyle(host.children[state.activeMirrors.length + i], m.el, m.isFixed);
                }
            }
        };

        const loop = () => { update(); frameId = requestAnimationFrame(loop); };
        loop();

        window.addEventListener('scroll', update, true);
        return () => { cancelAnimationFrame(frameId); window.removeEventListener('scroll', update, true); };
    }, []);

    const toggleDisablePlus = (block, outlineEl) => {
        if (!outlineEl) return;

        if (!block) {
            outlineEl.classList.remove('__disable-plus');
            return;
        }

        const wrapper = block.closest('section-wrapper');
        let isAvailable = true; // Mặc định là true (không disable)

        if (wrapper) {
            // Lấy thẻ section bên trong section-wrapper (ưu tiên thẻ <section> hoặc con đầu tiên)
            const section = wrapper.querySelector('section') || wrapper.firstElementChild;

            // Đọc attribute, nếu = 'false' thì gán isAvailable = false
            if (section && section.getAttribute('data-block-available') === 'false') {
                isAvailable = false;
            }
        }

        // Nếu KHÔNG available (!isAvailable) thì thêm class __disable-plus, ngược lại thì gỡ bỏ
        outlineEl.classList.toggle('__disable-plus', !isAvailable);
    };

    const resetHover = () => {
        clearTimeout(state.hoverTimer);
        state.hoverEl = null;
        state.hoverIsFixed = false;
        state.hoverSectionId = null;
        state.hoverBlockId = null;
        state.hoverPrimaryIndex = -1;
        state.hoverDOMPath = null;
        state.hoverMirrors = [];
        if (refs.hoverOut.current) refs.hoverOut.current.style.display = 'none';
    };

    const updateLabels = (block, isHover) => {
        const blockName = block.getAttribute('smind-block-name') || 'Block';
        const secName = block.closest('section-wrapper')?.getAttribute('data-name') || 'Section';

        const targetTxt = isHover ? refs.hoverTxt : refs.activeTxt;
        const targetSecTxt = isHover ? refs.hoverSecTxt : refs.activeSecTxt;

        if (targetTxt.current) targetTxt.current.innerText = blockName;
        if (targetSecTxt.current) targetSecTxt.current.innerText = secName;
    };

    const activateBlock = (block) => {
        if (!block) {
            state.activeEl = null;
            state.activeSectionId = null;
            state.activeBlockId = null;
            state.activePrimaryIndex = -1;
            state.activeDOMPath = null;
            state.activeMirrors = [];
            if (refs.activeOut.current) {
                refs.activeOut.current.style.display = 'none';
                refs.activeOut.current.classList.remove('__active', '__show-actions');
            }
            return;
        }

        const wrapper = block.closest('section-wrapper');
        state.activeEl = block;
        state.activeIsFixed = isFixedElement(block);
        state.activeSectionId = wrapper?.getAttribute('section-id') || null;
        state.activeBlockId = block.getAttribute('smind-block-id') || null;
        state.activePrimaryIndex = getBlockIndex(block);
        state.activeDOMPath = getDOMPath(block, wrapper);
        state.activeMirrors = buildMirrors(block);
        updateLabels(block, false);

        if (onClick) {
            const parentSection = block.closest('section-wrapper');
            if (parentSection) {
                const sectionId = parentSection.getAttribute('section-id');
                const blockId = block.getAttribute('smind-block-id');
                onClick(sectionId, blockId);
            }
        }

        resetHover();
        if (refs.activeOut.current) refs.activeOut.current.classList.add('__active', '__show-actions');
    };

    useEffect(() => {
        if (activeId) {
            const targetSection = document.querySelector(`section-wrapper[section-id="${activeId?.sectionId}"]`);
            if (targetSection) {
                // Prefer the exact DOM node the user clicked — this avoids the duplicate-DOM
                // bug (e.g. slider clones share the same smind-block-id and querySelector
                // would always pick the first match).
                const clickedBlock = lastClickedBlockRef.current;
                const matchesClick = clickedBlock?.isConnected &&
                    clickedBlock.getAttribute('smind-block-id') === activeId?.blockId &&
                    clickedBlock.closest('section-wrapper')?.getAttribute('section-id') === activeId?.sectionId;

                const targetBlock = matchesClick
                    ? clickedBlock
                    : targetSection.querySelector(`[smind-block-id="${activeId?.blockId}"]`);

                if (targetBlock && targetBlock !== state.activeEl) {
                    activateBlock(targetBlock);
                    setSelectedElementId(true);
                    setTimeout(() => {
                        setSelectedElementId(false);
                    }, 10);
                    window.dispatchEvent(new CustomEvent('smindDeactivateSection'));
                } else if (!targetBlock) {
                    activateBlock(null);
                }
            } else {
                activateBlock(null);
            }
        } else {
            activateBlock(null);
        }
    }, [activeId]);

    useEffect(() => {
        const handleMouse = (e) => {
            const isClick = e.type === 'click';

            const outlineSection = e.target.closest('.smind-outline-section');
            const outlineBlock = e.target.closest('.smind-outline-block');

            // ==========================================
            // 💡 KIỂM TRA: CÓ CHẠM VÀO ĐÚNG UI CỦA MÌNH HAY KHÔNG?
            // (Đề phòng nhận nhầm outlineBlock của Element thành của mình)
            // ==========================================
            const isOwnHoverUI = refs.hoverOut.current && refs.hoverOut.current.contains(e.target);
            const isOwnActiveUI = refs.activeOut.current && refs.activeOut.current.contains(e.target);
            const isOwnUI = isOwnHoverUI || isOwnActiveUI;

            // ==========================================
            // 💡 XỬ LÝ CLICK VÀO LABEL CỦA UI CHÍNH CHỦ
            // ==========================================
            if (outlineSection || isOwnUI) {
                if (isClick && isOwnUI) {
                    const targetEl = state.hoverEl || state.activeEl;

                    // 1. Nếu click trúng cái nhãn Section (nằm bên trong Label)
                    if (e.target.closest('.smind-sub-label.__section')) {
                        if (targetEl) {
                            const parentSection = targetEl.closest('section-wrapper');
                            if (parentSection) {
                                const sectionId = parentSection.getAttribute('section-id');
                                if (onSectionClick) onSectionClick(sectionId);
                                activateBlock(null);

                                // Bắn sự kiện ra toàn trang để OutlineSection bắt lấy
                                window.dispatchEvent(new CustomEvent('smindActivateSection', { detail: sectionId }));
                            }
                        }
                    }
                    // 2. Nếu click vào các phần khác của Label (để active Block)
                    else if (e.target.closest('.smind-label')) {
                        if (targetEl) {
                            activateBlock(targetEl);
                        }
                    }
                }

                // Nếu rê chuột vào đúng UI Outline của mình, huỷ lệnh tắt viền mờ
                if (!isClick && isOwnUI) {
                    clearTimeout(state.hoverTimer);
                }
                return;
            }

            // ==========================================
            // 🌟 NẾU CHUỘT CHẠM VÀO ELEMENT -> TẮT BLOCK ĐI NGAY LẬP TỨC
            // ==========================================
            if (e.target.closest('[smind-attributes]:not([smind-block-id])')) {
                if (isClick) {
                    activateBlock(null);
                } else {
                    if (state.pendingHoverEl !== 'ELEMENT_HOVER') {
                        clearTimeout(state.hoverTimer);
                        resetHover(); // Tắt luôn không đợi 150ms để nhường Element
                        state.pendingHoverEl = 'ELEMENT_HOVER';
                    }
                }
                return;
            }

            const block = e.target.closest('[smind-block-id]');

            if (refs.activeOut.current && !isClick) {
                refs.activeOut.current.classList.toggle('__show-actions', !!block && block === state.activeEl);
            }

            if (!block) {
                if (isClick) activateBlock(null);
                else {
                    if (state.pendingHoverEl !== 'OUTSIDE') {
                        clearTimeout(state.hoverTimer);
                        state.pendingHoverEl = 'OUTSIDE';
                        state.hoverTimer = setTimeout(() => {
                            resetHover();
                        }, 150); // Đi ra ngoài khu vực bình thường thì delay 150ms
                    }
                }
                return;
            }

            if (isClick) {
                // Remember the exact DOM node so useEffect([activeId]) can prefer this over
                // querySelector (which picks the first match across duplicate DOMs).
                lastClickedBlockRef.current = block;
                activateBlock(block);
                return;
            }

            if (block === state.hoverEl) return;

            // Xoá timer hiện tại nếu có
            clearTimeout(state.hoverTimer);

            if (block !== state.activeEl) {
                state.pendingHoverEl = block;
                state.hoverTimer = setTimeout(() => {
                    // Reset cái hover cũ nếu có (dọn dẹp sạch)
                    if (state.hoverEl) {
                        resetHover();
                    }

                    const hoverWrapper = block.closest('section-wrapper');
                    state.hoverEl = block;
                    state.hoverIsFixed = isFixedElement(block);
                    state.hoverSectionId = hoverWrapper?.getAttribute('section-id') || null;
                    state.hoverBlockId = block.getAttribute('smind-block-id') || null;
                    state.hoverPrimaryIndex = getBlockIndex(block);
                    state.hoverDOMPath = getDOMPath(block, hoverWrapper);
                    state.hoverMirrors = buildMirrors(block);
                    updateLabels(block, true);
                    toggleDisablePlus(block, refs.hoverOut.current);
                }, 100); // Set lên 100ms để nó nhận diện nhạy hơn
            } else {
                resetHover();
            }
        };

        // 🌟 1. THÊM HÀM NÀY: Xử lý khi chuột bay hẳn ra ngoài Iframe/Preview
        const handleMouseLeaveDocument = () => {
            clearTimeout(state.hoverTimer);
            state.pendingHoverEl = 'OUTSIDE';
            resetHover(); // Lập tức tắt viền hover
        };

        const handleForceActive = (e) => {
            const { sectionId, blockId } = e.detail;
            if (!sectionId && !blockId) return;            

            const targetSection = document.querySelector(`section-wrapper[section-id="${sectionId}"]`);
            if(targetSection) {
                const targetBlock = targetSection.querySelector(`[smind-block-id="${blockId}"]`);
                if (targetBlock) {
                    activateBlock(targetBlock);
                    window.dispatchEvent(new CustomEvent('smindDeactivateSection'));
                    window.dispatchEvent(new CustomEvent('smindDeactivateElement'));
                }
            }            
        };

        // Hàm tắt Block được gọi từ Element khi Element kích hoạt
        const handleForceDeactivate = () => activateBlock(null);

        document.addEventListener('mouseover', handleMouse);
        document.addEventListener('mouseleave', handleMouseLeaveDocument);
        document.addEventListener('click', handleMouse);
        window.addEventListener('smindActivateBlock', handleForceActive);
        window.addEventListener('smindDeactivateBlock', handleForceDeactivate);

        return () => {
            document.removeEventListener('mouseover', handleMouse);
            document.removeEventListener('mouseleave', handleMouseLeaveDocument);
            document.removeEventListener('click', handleMouse);
            window.removeEventListener('smindActivateBlock', handleForceActive);
            window.removeEventListener('smindDeactivateBlock', handleForceDeactivate);
            clearTimeout(state.hoverTimer);
        };
    }, [onClick, onSectionClick]); // 💡 Cập nhật dependency

    const triggerAction = (actionCallback, ...params) => {
        const blockId = state?.activeEl?.getAttribute('smind-block-id');
        const sectionId = state?.activeEl?.closest('section-wrapper')?.getAttribute('section-id');
        if (actionCallback && sectionId && blockId) actionCallback(sectionId, blockId, ...params);
    };

    const triggerPlus = (actionCallback, ...params) => {
        const blockId = state?.hoverEl?.getAttribute('smind-block-id');
        const sectionId = state?.hoverEl?.closest('section-wrapper')?.getAttribute('section-id');
        if (actionCallback && sectionId) actionCallback(sectionId, blockId, ...params);
    };

    return (
        <>
            <div className="smind-outline-block --hover" ref={refs.hoverOut}>
                <OutlineLabelBlock textRef={refs.hoverTxt} textSectionRef={refs.hoverSecTxt} />
                <OutlinePlusBlock
                    onAdd={(actionType, direction) => triggerPlus(onAdd, actionType, direction)}
                />
            </div>

            <div className="smind-outline-block --active" ref={refs.activeOut}>
                <OutlineLabelBlock textRef={refs.activeTxt} textSectionRef={refs.activeSecTxt} />
                <OutlineActionsBlock
                    onHide={() => { triggerAction(onHide); activateBlock(null); }}
                    onRemove={() => { triggerAction(onRemove); activateBlock(null); }}
                />
            </div>

            {/* Mirror outlines cho các block trùng smind-block-id trong cùng section (slider clones...) */}
            <div ref={refs.mirrorHost} />
        </>
    );
}

export default OutlineBlock;